import { babillardReadyAtom, isInsideOnboardingAtom, isLoggedInAtom, isOnboardingAtom, userInfoAtom } from 'app/atoms';
import { useAppDidStart } from 'app/lib/query';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { useAllAddresses } from 'app/network/address';
import { useUserTypesConfig, useVerifyRequiredValues } from 'app/network/user';
import { useHomeExploreTutorial } from 'app/hooks/use-home-explore-tutorial';
import { useAppStartModals } from 'app/screens/intro/intro-modal';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { useEffect, useRef } from 'react';

/*
  This hook is responsible for setting up the landing page, which includes:
  - Marking the babillard as ready (so that the landing page can be shown)
  - Navigating to the "choose user type" screen if the user is not logged in and there are user types available (eg. Partage Agricole)
  - Navigating to the "public user welcome" screen after sign-up if the user is a public user and has an address
  - Triggering the app start modals flow for logged-in users (intro, newly-insured, custom modals, closest offers prompt, etc.)
*/
export function useLandingPageSetup({ navigation }: { navigation: SpecificNavigationProp<'landing-page'> }) {
  useHomeExploreTutorial();

  const isLoggedIn = useAtomValue(isLoggedInAtom);

  const setBabillardReady = useSetAtom(babillardReadyAtom);
  useEffect(() => {
    setBabillardReady(true);
  }, [setBabillardReady]);

  const { data: userTypesConfig, isPending: isUserTypesConfigPending } = useUserTypesConfig();
  useRegisterLoading(isUserTypesConfigPending, 'LandingPageScreen-user-types');

  // Show the modal to choose the user type if the user is not logged in (eg. Partage Agricole)
  useEffect(() => {
    if (userTypesConfig && userTypesConfig.length > 0 && !isLoggedIn) {
      navigation.navigate('chooseUserType', undefined, { pop: true });
    }
  }, [isLoggedIn, navigation, userTypesConfig]);

  const userInfo = useAtomValue(userInfoAtom);
  const isOnboarding = useAtomValue(isOnboardingAtom);
  const isInsideOnboarding = useAtomValue(isInsideOnboardingAtom);

  const { data: addresses } = useAllAddresses();
  const hasAddress = addresses && addresses.length > 0;

  const [publicUserWelcomeShown, setPublicUserWelcomeShown] = useStoredValue(StorageKey.PublicUserWelcomeShown, false);

  // After sign-up, show the modal to ask the user to be patient
  useEffect(() => {
    if (
      userInfo?.type === 'public' &&
      hasAddress &&
      !isOnboarding &&
      !isInsideOnboarding &&
      publicUserWelcomeShown === false
    ) {
      setPublicUserWelcomeShown(true);
      navigation.navigate('publicUserWelcome', undefined, { pop: true });
    }
  }, [
    hasAddress,
    isOnboarding,
    isInsideOnboarding,
    navigation,
    publicUserWelcomeShown,
    setPublicUserWelcomeShown,
    userInfo?.type,
  ]);

  const { appDidStart, appDidStop } = useAppDidStart();
  const initialAppLifecycle = useRef({ appDidStart, appDidStop });
  useEffect(() => {
    const lifecycle = initialAppLifecycle.current;
    lifecycle.appDidStart();
    return lifecycle.appDidStop;
  }, []);

  // Will verify that the user has filled all required values (profile, address, etc.)
  useVerifyRequiredValues();
  // Will (maybe) show one of the various popups available to logged-in users: intro, newly-insured, custom (Utile), prompt-to-borrow (closest-offers), etc.
  useAppStartModals();
}
